import {container, inject, singleton} from "tsyringe";
import {BulkCommand} from "../../commands/bulk.command";
import {ICommand} from "../../commands/i-command.interface";
import {Entity} from "../../domain/entities/entity";
import {EntityManager} from "../../persistence/entity.manager";
import {CommandBus} from "../../services/command-bus.service";
import {ClassOf} from "../../types/class-of.type";
import {AbstractBaseListener} from "../abstract-base.listener";
import {BaseMessage} from "../base-message.interface";
import {MessageBroker} from "../message-broker";
import {OnMessage} from "../on-message.decorator";
import {CommandSendPayload} from "../payloads/command-send.payload";
import {EntityChangedPayload} from "../payloads/entity-changed.payload";
import {EntityCreatedPayload} from "../payloads/entity-created.payload";
import {EntityDeletedPayload} from "../payloads/entity-deleted.payload";

const bulkCommandsOnly = (message: BaseMessage<CommandSendPayload>): boolean => message.payload.command instanceof BulkCommand;

const withOutBulkCommands = (message: BaseMessage<CommandSendPayload>): boolean => !bulkCommandsOnly(message);

type VerbHandler = (entity: Entity, messageBroker: MessageBroker) => void;

/**
 * One handler per verb `onCommandSend` understands — a `Map` instead of a
 * `switch` so `MATCHING_VERBS` below can be DERIVED from its keys instead of
 * duplicating them by hand (review 03, WARNING, Linse B, 2026-08-18: the
 * previous hand-written `Set` could silently drift from the switch's actual
 * cases — a forgotten entry there would recreate exactly the silent-skip
 * problem this file's `instanceof Entity` guard exists to make observable).
 *
 * A `Map`, NOT a plain object literal (`Record<string, VerbHandler>`):
 * review 04, MAJOR, Linse B, 2026-08-18 found that an object literal inherits
 * from `Object.prototype`, so an `action` value colliding with an inherited
 * member name (`__proto__`, `toString`, `constructor`, ...) would either
 * throw (`__proto__` resolves to `Object.prototype` itself, which isn't
 * callable) or silently invoke the wrong, inherited function — a regression
 * the original `switch` could never have, since an unmatched `action` there
 * just fell through as a safe no-op. A `Map` has no such prototype-chain
 * lookup for its keys.
 */
const VERB_HANDLERS = new Map<string, VerbHandler>([
    [`create`, (entity, messageBroker) => { // entity was created
        messageBroker.publish<EntityCreatedPayload<Entity>>({
            type: `entity.created`,
            identityHash: entity.uid,
            payload: {
                uid: entity.uid,
                entity,
                prototype: entity.constructor as ClassOf<Entity>,
            }
        });
    }],
    [`update`, (entity, messageBroker) => { // entity was updated
        messageBroker.publish<EntityChangedPayload<Entity>>({
            type: `entity.changed`,
            identityHash: entity.uid,
            payload: {
                uid: entity.uid,
                prototype: entity.constructor as ClassOf<Entity>,
                changeSet: entity.changeSet
            }
        });
    }],
    [`delete`, (entity, messageBroker) => { // entity was deleted
        messageBroker.publish<EntityDeletedPayload<Entity>>({
            type: `entity.deleted`,
            identityHash: entity.uid,
            payload: {
                entity
            }
        });
    }],
]);

/** The verbs `VERB_HANDLERS` actually handles — derived, not hand-duplicated. */
const MATCHING_VERBS = new Set(VERB_HANDLERS.keys());

/**
 * A handler that reads the COMMAND instead of an entity payload.
 *
 * `VERB_HANDLERS` above can only ever see verbs whose command carries a live `Entity`. Some
 * commands legitimately carry a flat row and still have to produce an entity.* event — `restore`
 * is the one (O2D-PRD-101). Those live here, and they run BEFORE the `instanceof Entity` guard.
 */
type CommandVerbHandler = (command: ICommand<unknown>, messageBroker: MessageBroker) => void;

/**
 * The whitelist key is snake_case (`knowledge_object`, RestoreTypeMap.php), while
 * `EntityManager.getRepositoryForEntityByName` compares against `StringHelper.toKebabCase` of the
 * class name (`entity.manager.ts:91-98`) — `KnowledgeObject` becomes `knowledge-object`. One
 * underscore swap is the whole conversion; anything cleverer would just be a second, divergent
 * spelling rule.
 */
const snakeToKebab = (entityType: string): string => entityType.replace(/_/g, `-`);

/**
 * Handlers that need the command, not an entity — consulted BEFORE the `instanceof Entity`
 * guard below, and `continue`d on afterwards so the guard never sees them.
 *
 * WHY the restore case had to move here: the guard at the top of the loop rejects any command
 * whose payload is not an `Entity` instance, and `RestoreEntityCommand` now carries a flat
 * `RestorableRow`. Left where it was, every single restore would have produced a `console.warn`
 * and no event at all — the refresh would silently never happen, which is exactly the class of
 * failure the guard's warning exists to make visible.
 */
const COMMAND_VERB_HANDLERS = new Map<string, CommandVerbHandler>([
    [`restore`, (command, messageBroker) => { // soft-deleted entity was restored (O2D-PRD-101)
        // Reuse entity.changed so the type-scoped stores/views refresh. The payload is a flat
        // row, so the class comes from the SAME register the repositories use, resolved by name.
        const repository = container.resolve(EntityManager)
            .getRepositoryForEntityByName(snakeToKebab(command.mainEntityClass));

        // Not every restorable type has a registered frontend entity — `linkview` deliberately
        // has none (it is served by BackendLinkviewRepository, which listens for the restore
        // command itself and reloads its own board list). Publishing an entity.changed without a
        // prototype would be a malformed message, so nothing is published; that is not an error.
        if (!repository) {
            return;
        }

        messageBroker.publish<EntityChangedPayload<Entity>>({
            type: `entity.changed`,
            identityHash: command.mainEntityUid,
            payload: {
                uid: command.mainEntityUid,
                prototype: repository.entity as ClassOf<Entity>,
                // Empty on purpose and harmless: every reader only ever asks whether a given KEY
                // is present (`entity-change.listener.ts:35/39`,
                // `abstract-ha-timeline.view.ts:87/135`), and a freshly hydrated entity hands
                // them `{}` today as well.
                changeSet: {},
            }
        });
    }],
]);

@singleton()
export class CommandSendListener extends AbstractBaseListener {

    constructor(
        @inject(CommandBus) private readonly _commandBus: CommandBus
    ) {
        super();
    }

    /**
     * A bulk is NOT unwrapped back into the broker — it only has to be reported when it failed.
     *
     * It used to re-publish command.send once per inner command. Those messages reached
     * onCommandSend below, which emitted a SECOND entity.* for entities BulkCommandHandler had
     * already announced. Measured in the browser on 2026-09-18: one save of an issue published
     * entity.created twice (60ms from the handler, 560ms from here), each one made the tasklist
     * reload and restore its scroll anchor, and the second restore moved the list under the user
     * about 790ms after the save; deleting behaved the same way (61ms / 561ms).
     *
     * Dropping this and not the handler's publish is deliberate: the handler maps create and
     * delete to their own event and EVERY OTHER verb to entity.changed, while VERB_HANDLERS below
     * knows only create/update/delete. Thirty command names carry other verbs — rename.keyword,
     * recolor.issue-status, reorder.link-tile, finalize.issue, save.knowledge-object-content among
     * them — and inside a bulk the handler always was their only source. Nothing else consumed the
     * unwrapped messages either: command.send has exactly two subscribers, both in this class.
     */
    @OnMessage(`command.send`, bulkCommandsOnly)
    public onBulkCommandSend(messages: BaseMessage<CommandSendPayload>[]): void {

        for (const message of messages) {
            if (!message.payload.success) {
                throw new Error(`Unsuccessful command: «${message.payload.command}»`);
            }
        }
    }

    @OnMessage(`command.send`, withOutBulkCommands)
    public onCommandSend(messages: BaseMessage<CommandSendPayload>[]): void {
        for (const message of messages) {
            const command = message.payload.command;
            const [action] = command.name.split(`.`);

            // BEFORE the Entity guard — these handlers read the command itself and a flat
            // payload is their normal case, not a defect. `continue` so the guard below never
            // sees them and never warns about them.
            const commandVerbHandler = COMMAND_VERB_HANDLERS.get(action);
            if (commandVerbHandler) {
                commandVerbHandler(command, this._messageBroker);
                continue;
            }

            if (!(command.payload instanceof Entity)) {
                // command.payload ist kein Entity (z.B. ein Command mit eigenem DTO-Payload wie
                // UpdateKnowledgeObjectMetadataCommand). Nur Commands mit passendem Verb würden
                // unten fälschlich gecastet und eine kaputte entity.*-Nachricht erzeugen
                // (identityHash: undefined, prototype: Object). Commands mit dediziertem Handler +
                // eigenem DTO-Payload veröffentlichen ihr entity.*-Event bereits selbst korrekt.
                // Sicher geprüft: kein handler-loser Command mit passendem Verb hat einen
                // Nicht-Entity-Payload (2026-08-17).
                //
                // Der console.warn hier feuert NUR, wenn BEIDES zutrifft: das Verb würde in
                // VERB_HANDLERS vorkommen UND der Command hat KEINEN dedizierten Handler
                // (CommandBus.hasRegisteredHandler) — dann wäre diese generische Fallback-
                // Publikation die EINZIGE Quelle für ein entity.*-Event, und ihr Überspringen
                // wäre ein echtes Problem. Für Commands MIT dediziertem Handler (wie
                // UpdateKnowledgeObjectMetadataCommand) ist das Überspringen dagegen der
                // erwartete Normalfall — der Handler publiziert sein entity.*-Event bereits
                // selbst. Ohne diesen zweiten Check hätte die Warnung bei JEDEM einzelnen,
                // funktional korrekten Aufruf eines solchen Commands gefeuert (z.B. bei jedem
                // Umbenennen eines KnowledgeObjects) — Cry-Wolf-Rauschen ohne Diagnosewert,
                // gefunden 2026-08-18 im vierten Review-Lauf genau dieser Änderung, siehe
                // CommandBus.hasRegisteredHandler()'s Docblock.
                if (MATCHING_VERBS.has(action) && !this._commandBus.hasRegisteredHandler(command)) {
                    console.warn(
                        `[command-send.listener] Command "${command.name}" hat ein passendes Verb (${action}), `
                        + `keinen dedizierten Handler und command.payload ist keine Entity-Instanz — die `
                        + `generische entity.*-Fallback-Publikation wird übersprungen, obwohl sie hier die `
                        + `einzige Quelle für ein entity.*-Event wäre. Live-Refresh-Listener bemerken dieses `
                        + `Update nie.`,
                        command,
                    );
                }
                continue;
            }
            const entity = command.payload;

            VERB_HANDLERS.get(action)?.(entity, this._messageBroker);
        }
    }

}